
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Address, Location, Zone } from '@clients/myTrips'
import LocationAddress from '@/models/LocationAddress';
import IconGeolocation from '@components/icons/icon-geolocation.svg';
import { featureToAddress, featureToLocation } from '@util/mapbox';

const GeographicsModule = namespace('Geographics');

interface SearchResult {
  name: string,
  address: Address,
  location: Location
}

@Component({
  name: 'AddressInputField',
  components: {
    IconGeolocation
  }
})

export default class AutocompleteAddress extends Vue {
  $refs!: {
    address: HTMLFormElement
  }

  @Prop()
  private value: LocationAddress;

  @Prop()
  private id: String;

  @Prop({ default: false })
  private disabled: boolean;

  @Prop({ default: () => [] }) zones: Zone[];

  private input: string = this.getLocationName();
  private searchResults: SearchResult[] = [];
  private userLocation: Location = null;
  private resultListOpen = false;
  private locationButtonDisabled = false;
  private showLocationError = false;
  private locationSharingPermissionError = false;
  private arrowCounter = -1;

  @GeographicsModule.Action('fetchAutocompletePlaces')
  private fetchAutocompletePlaces;

  @GeographicsModule.Getter('getAutocompletePlaces')
  private getAutocompletePlaces;

  getLocationName() {
    if (this.value && this.value.address && this.value.address.moniker) {
      return this.value.address.moniker;
    }
    return '';
  }

  @Watch('value', { immediate: true })
  onValueChange(newValue: LocationAddress) {
    this.closeAndReset();
    if (newValue && newValue.address && newValue.address.moniker) {
      this.input = newValue.address.moniker;
    }
  }

  onArrowDown() {
    if (this.arrowCounter < this.searchResults.length - 1) {
      this.arrowCounter = this.arrowCounter + 1;
    } else if (this.arrowCounter == this.searchResults.length - 1) {
      this.arrowCounter = 0;
    }
  }

  onArrowUp() {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    } else if (this.arrowCounter == 0 && this.searchResults.length > 1) {
      this.arrowCounter = this.searchResults.length - 1;
    }
  }

  closeAndReset() {
    this.closeAutocomplete();
    this.resetToOriginalValue();
  }

  closeAutocomplete() {
    this.arrowCounter = -1;
    this.resultListOpen = false;
  }

  resetToOriginalValue() {
    this.input = this.getLocationName();
  }

  public focusField() {
    this.$refs.address.focus();
  }

  private async updateSearchResults() {
    this.showLocationError = false;
    this.locationSharingPermissionError = false;
    if (this.input.length >= 2) {
      const matchingZones = this.zones
        .filter(z => z.name.toLowerCase().includes(this.input.toLowerCase()))
        .map(z => ({
          name: z.name,
          address: z.address,
          location: { latitude: z.latitude, longitude: z.longitude }
        }))

      this.searchResults = matchingZones;

      if (matchingZones.length === 0 && this.input.length >= 3) {
        const results = await this.fetchAutocompletePlaces({ search: this.input,
          lastLocation: this.userLocation ? {
            lon: this.userLocation.longitude,
            lat: this.userLocation.latitude
          } : null,
          poi: true
        });

        this.searchResults = results.features.map(r => ({
          name: r.place_name_de,
          address: featureToAddress(r),
          location: featureToLocation(r)
        }));
      }

      if (this.searchResults.length === 1 && this.searchResults[0].name.toLocaleLowerCase() === this.input.toLocaleLowerCase()) {
        this.commitResult(this.searchResults[0]);
      } else {
        this.resultListOpen = true;
        this.arrowCounter = 0;
      }
    } else {
      this.closeAutocomplete();
    }
  }

  private handleSearchExit() {
    if (this.value && this.input.length === 0 && this.value.address) {
      this.input = this.value.address.moniker;
    }
  }

  private commitSelectedSearchResult() {
    if (this.resultListOpen) {
      if (this.searchResults[this.arrowCounter] !== undefined) {
        this.commitResult(this.searchResults[this.arrowCounter])
      } else if (this.searchResults.length > 0) {
        this.commitResult(this.searchResults[0])
      }
    }
  }

  private commitSearchResult(result: SearchResult) {
    this.commitResult(result);
  }

  private async currentLocation() {
    this.locationButtonDisabled = true;
    this.userLocation = await this.getCurrentLocation();
    if (this.userLocation && this.userLocation.longitude && this.userLocation.latitude) {
      this.$emit('currentLocation', this.userLocation);
      this.showLocationError = false;
      this.locationSharingPermissionError = false;
    } else {
      this.showLocationError = true;
    }
    this.locationButtonDisabled = false;
  }

  async commitResult(result: SearchResult) {
    this.input = result.address.moniker;
    this.$emit('input', { ...result });
    this.closeAutocomplete();
  }

  handleClickOutside(evt) {
    if (!this.$el.contains(evt.target))
      this.closeAndReset();
  }

  private async getCurrentLocation(): Promise<Location> {
    try {
      const { lat, lng, } = await this.$getLocation({
        enableHighAccuracy: true,
        timeout: 2500,
        maximumAge: 300000
      });
      return { latitude: lat, longitude: lng };
    } catch (error) {
      const result = await navigator.permissions.query({ name: 'geolocation' })
      if (result.state === 'denied') {
          this.locationSharingPermissionError = true;
      }

      return null;
    }
  }
}
